/**
 * Centro de Operaciones — composición de alertas desde datos existentes (sin nuevos cálculos de negocio).
 */
import { CRM_ESTADOS } from './crmCuentaCorriente';

export const CO_PRIORIDAD = {
  ROJO: 'rojo',
  NARANJA: 'naranja',
  AMARILLO: 'amarillo',
  VERDE: 'verde'
};

const ORDEN_PRIORIDAD = {
  [CO_PRIORIDAD.ROJO]: 0,
  [CO_PRIORIDAD.NARANJA]: 1,
  [CO_PRIORIDAD.AMARILLO]: 2,
  [CO_PRIORIDAD.VERDE]: 3
};

export function ordenarPorPrioridad(items) {
  return [...(items || [])].sort((a, b) => {
    const pa = ORDEN_PRIORIDAD[a.prioridad] ?? 99;
    const pb = ORDEN_PRIORIDAD[b.prioridad] ?? 99;
    if (pa !== pb) return pa - pb;
    return (b.peso ?? 0) - (a.peso ?? 0);
  });
}

export function buildTarjetasPrioritarias({
  clientesVencidos = 0,
  montoVencido = 0,
  sinStock = 0,
  stockCritico = 0,
  cierrePendiente = false,
  comprasPendientes = 0
}) {
  const tarjetas = [];

  if (clientesVencidos > 0) {
    tarjetas.push({
      id: 'clientes-vencidos',
      titulo: 'Clientes vencidos',
      valor: clientesVencidos,
      detalle: montoVencido > 0 ? `Deuda vencida acumulada` : 'Requieren seguimiento de cobro',
      monto: montoVencido,
      prioridad: CO_PRIORIDAD.ROJO,
      peso: montoVencido,
      to: '/cuenta-corriente'
    });
  }

  if (sinStock > 0) {
    tarjetas.push({
      id: 'sin-stock',
      titulo: 'Productos sin stock',
      valor: sinStock,
      detalle: 'Riesgo de quiebre en ventas',
      prioridad: CO_PRIORIDAD.ROJO,
      peso: sinStock * 1000,
      to: '/stock'
    });
  }

  if (stockCritico > 0) {
    tarjetas.push({
      id: 'stock-critico',
      titulo: 'Stock crítico',
      valor: stockCritico,
      detalle: 'Productos bajo mínimo en sucursal',
      prioridad: CO_PRIORIDAD.NARANJA,
      peso: stockCritico * 100,
      to: '/stock'
    });
  }

  if (cierrePendiente) {
    tarjetas.push({
      id: 'cierre-caja',
      titulo: 'Cierre de caja pendiente',
      valor: '—',
      detalle: 'El día operativo aún no fue cerrado',
      prioridad: CO_PRIORIDAD.NARANJA,
      peso: 500,
      to: '/caja'
    });
  }

  if (comprasPendientes > 0) {
    tarjetas.push({
      id: 'compras-pendientes',
      titulo: 'Compras pendientes',
      valor: comprasPendientes,
      detalle: 'Órdenes sin recepcionar',
      prioridad: CO_PRIORIDAD.AMARILLO,
      peso: comprasPendientes,
      to: '/compras'
    });
  }

  if (!tarjetas.length) {
    tarjetas.push({
      id: 'todo-ok',
      titulo: 'Operación al día',
      valor: '✓',
      detalle: 'No hay alertas prioritarias detectadas',
      prioridad: CO_PRIORIDAD.VERDE,
      peso: 0,
      to: '/dashboard'
    });
  }

  return ordenarPorPrioridad(tarjetas);
}

export function buildAlertasOperativas({
  clientesVencidos = 0,
  montoVencido = 0,
  sinStock = 0,
  stockCritico = 0,
  productosStockBajo = [],
  cierrePendiente = false,
  comprasPendientes = 0,
  cobrosHoy = 0
}) {
  const alertas = [];

  if (clientesVencidos > 0) {
    alertas.push({
      id: 'alert-cc-vencidos',
      titulo: `${clientesVencidos} cliente${clientesVencidos === 1 ? '' : 's'} con deuda vencida`,
      impacto: 'Cobranza retrasada — flujo de caja',
      accion: 'Ir a Cuenta Corriente',
      to: '/cuenta-corriente',
      prioridad: CO_PRIORIDAD.ROJO,
      peso: montoVencido
    });
  }

  if (sinStock > 0) {
    alertas.push({
      id: 'alert-sin-stock',
      titulo: `${sinStock} producto${sinStock === 1 ? '' : 's'} sin stock`,
      impacto: 'Ventas pueden fallar o quedar pendientes',
      accion: 'Revisar inventario',
      to: '/stock',
      prioridad: CO_PRIORIDAD.ROJO,
      peso: sinStock * 1000
    });
  }

  if (stockCritico > 0) {
    const muestra = (productosStockBajo || []).slice(0, 2).map((p) => p.nombre || p.producto_nombre).filter(Boolean);
    alertas.push({
      id: 'alert-stock-bajo',
      titulo: `${stockCritico} producto${stockCritico === 1 ? '' : 's'} bajo mínimo`,
      impacto: muestra.length ? `Ej.: ${muestra.join(', ')}` : 'Reposición recomendada',
      accion: 'Ver stock',
      to: '/stock',
      prioridad: CO_PRIORIDAD.NARANJA,
      peso: stockCritico * 100
    });
  }

  if (cierrePendiente) {
    alertas.push({
      id: 'alert-cierre-caja',
      titulo: 'Cierre diario de caja pendiente',
      impacto: 'Conciliación del día incompleta',
      accion: 'Ir a Caja',
      to: '/caja',
      prioridad: CO_PRIORIDAD.NARANJA,
      peso: 800
    });
  }

  if (comprasPendientes > 0) {
    alertas.push({
      id: 'alert-compras',
      titulo: `${comprasPendientes} compra${comprasPendientes === 1 ? '' : 's'} pendiente${comprasPendientes === 1 ? '' : 's'}`,
      impacto: 'Mercadería en tránsito o sin confirmar',
      accion: 'Ver compras',
      to: '/compras',
      prioridad: CO_PRIORIDAD.AMARILLO,
      peso: comprasPendientes
    });
  }

  if (cobrosHoy > 0) {
    alertas.push({
      id: 'alert-cobros-hoy',
      titulo: 'Cobros de cuenta corriente registrados hoy',
      impacto: typeof cobrosHoy === 'number' && cobrosHoy < 1000
        ? `${cobrosHoy} operación${cobrosHoy === 1 ? '' : 'es'}`
        : 'Ingresos por cobranza en el día',
      accion: 'Ver cuenta corriente',
      to: '/cuenta-corriente',
      prioridad: CO_PRIORIDAD.VERDE,
      peso: 1
    });
  }

  if (!alertas.length) {
    alertas.push({
      id: 'alert-sin-novedades',
      titulo: 'Sin novedades operativas',
      impacto: 'Todo en orden por ahora',
      accion: 'Ver dashboard',
      to: '/dashboard',
      prioridad: CO_PRIORIDAD.VERDE,
      peso: 0
    });
  }

  return ordenarPorPrioridad(alertas);
}

/** Cuenta clientes vencidos desde filas CRM ya resueltas. */
export function contarClientesVencidosCrm(filasCrm) {
  return (filasCrm || []).filter((f) => f.estado === CRM_ESTADOS.VENCIDO).length;
}

export const ACCIONES_RAPIDAS = [
  { id: 'venta', label: 'Nueva venta', to: '/punto-venta', primary: true },
  { id: 'compra', label: 'Nueva compra', to: '/compras/nueva' },
  { id: 'productos', label: 'Productos', to: '/productos' },
  { id: 'clientes', label: 'Clientes', to: '/clientes' },
  { id: 'cc', label: 'Cuenta Corriente', to: '/cuenta-corriente' },
  { id: 'caja', label: 'Caja', to: '/caja' },
  { id: 'reportes', label: 'Reportes', to: '/reportes' }
];

export const ESTILOS_PRIORIDAD = {
  [CO_PRIORIDAD.ROJO]: {
    card: 'border-red-500/45 bg-red-500/10',
    badge: 'bg-red-600/30 text-red-100 border-red-500/50',
    dot: 'bg-red-400'
  },
  [CO_PRIORIDAD.NARANJA]: {
    card: 'border-orange-500/40 bg-orange-500/10',
    badge: 'bg-orange-500/25 text-orange-100 border-orange-500/40',
    dot: 'bg-orange-400'
  },
  [CO_PRIORIDAD.AMARILLO]: {
    card: 'border-amber-500/35 bg-amber-500/10',
    badge: 'bg-amber-500/20 text-amber-100 border-amber-500/35',
    dot: 'bg-amber-400'
  },
  [CO_PRIORIDAD.VERDE]: {
    card: 'border-emerald-500/35 bg-emerald-500/10',
    badge: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/35',
    dot: 'bg-emerald-400'
  }
};
